import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { apiConfigured, getSession } from "../api";
import type {
  AuditMemberRole,
  SharedAuditEngagement,
  SharedAuditQuery,
  SharedQueryAction,
} from "./collaboration-model";
import {
  QUERY_PRIORITIES,
  isQueryOverdue,
  type NewQueryInput,
} from "./queries";
import {
  collaborationApi,
  prepareEvidence,
  type AuditCompany,
  type MemberCandidate,
  type PortalLinkRecord,
} from "./collaboration-api";
import "./collaboration.css";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="ac-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
const statusLabel = (status: string) => status.replaceAll("-", " ");
const errorMessage = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "status" in error &&
    error.status === 409 &&
    error instanceof Error &&
    /query changed|revision/i.test(error.message)
  )
    return "Someone changed this query. Your input has been kept. Refresh shared queries, review the latest response, then retry.";
  return error instanceof Error
    ? error.message
    : "The request failed. Your input has been kept.";
};

export function AuditCollaboration({ onBack }: { onBack: () => void }) {
  const user = getSession()?.user;
  const [engagements, setEngagements] = useState<SharedAuditEngagement[]>([]);
  const [companies, setCompanies] = useState<AuditCompany[]>([]);
  const [selected, setSelected] = useState("");
  const [queryId, setQueryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [creatingEngagement, setCreatingEngagement] = useState(false);
  const [creatingQuery, setCreatingQuery] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const engagement = engagements.find((item) => item.id === selected);
  const detail = engagement?.queries.find((item) => item.query.id === queryId);
  const role = engagement?.members.find(
    (member) => member.userId === user?.sub,
  )?.role;
  const manages =
    user?.role === "firm_owner" || engagement?.createdBy === user?.sub;
  const canEdit =
    role === "editor" || role === "reviewer" || user?.role === "firm_owner";
  const canReview = role === "reviewer" || user?.role === "firm_owner";
  const replaceEngagement = (next: SharedAuditEngagement) =>
    setEngagements((items) =>
      items.some((item) => item.id === next.id)
        ? items.map((item) => (item.id === next.id ? next : item))
        : [...items, next],
    );
  const replaceQuery = (next: SharedAuditQuery) =>
    setEngagements((items) =>
      items.map((item) =>
        item.id === selected
          ? {
              ...item,
              queries: item.queries.some((q) => q.query.id === next.query.id)
                ? item.queries.map((q) =>
                    q.query.id === next.query.id ? next : q,
                  )
                : [...item.queries, next],
            }
          : item,
      ),
    );
  const run = async (action: () => Promise<void>, success = "") => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      if (success) setNotice(success);
      return true;
    } catch (failure) {
      setError(errorMessage(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!apiConfigured || !user) return;
    let live = true;
    setBusy(true);
    Promise.all([collaborationApi.list(), collaborationApi.companies()])
      .then(([items, entities]) => {
        if (live) {
          setEngagements(items);
          setCompanies(entities);
          setSelected(items[0]?.id ?? "");
        }
      })
      .catch((failure) => {
        if (live) setError(errorMessage(failure));
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [user?.sub]);
  const refresh = () =>
    run(async () => {
      const items = await collaborationApi.list();
      setEngagements(items);
    }, "Shared queries refreshed. Review the latest details before submitting retained input.");
  const today = new Date().toISOString().slice(0, 10);
  const allQueries = engagement?.queries ?? [];
  const visible = allQueries.filter(({ query }) => {
    const matches =
      `${query.reference} ${query.title} ${query.owner} ${query.area}`
        .toLowerCase()
        .includes(search.toLowerCase());
    return (
      matches &&
      (filter === "all" ||
        (filter === "overdue"
          ? isQueryOverdue(query, today)
          : filter === "blocking"
            ? query.blocksCompletion && query.status !== "closed"
            : query.status === filter))
    );
  });
  return (
    <main className="ac-root">
      <header className="ac-header">
        <div>
          <span className="ac-eyebrow">LEDZERLY · AUDITFLOW</span>
          <h1>Shared audit queries</h1>
          <p>
            Company-scoped engagements, authenticated team access and restricted
            client links.
          </p>
        </div>
        <button onClick={onBack}>Back to workpaper drafts</button>
      </header>
      <div className="ac-notice">
        Shared queries and evidence are saved to your firm's server. Workpaper
        drafts remain in this browser and are managed separately.
      </div>
      {!apiConfigured || !user ? (
        <section className="ac-panel">
          <h2>Server connection required</h2>
          <p>
            Sign in to a configured Ledzerly workspace to use shared queries.
            Browser evaluation identities cannot access this area.
          </p>
        </section>
      ) : (
        <>
          <div className="ac-identity">
            Signed in as <strong>{user.displayName}</strong> · {user.email}
          </div>
          {error && (
            <div role="alert" className="ac-error">
              {error}
            </div>
          )}
          {notice && (
            <div role="status" className="ac-success">
              {notice}
            </div>
          )}
          <fieldset disabled={busy} className="ac-controls">
            <div className="ac-toolbar">
              <Field label="Shared engagement">
                <select
                  value={selected}
                  onChange={(event) => {
                    setSelected(event.target.value);
                    setQueryId("");
                    setCreatingQuery(false);
                  }}
                >
                  <option value="">Select engagement</option>
                  {engagements.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {item.financialYear}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                onClick={() => setCreatingEngagement(!creatingEngagement)}
              >
                New shared engagement
              </button>
              <button onClick={refresh}>Refresh shared queries</button>
            </div>
            {creatingEngagement && (
              <EngagementForm
                companies={companies.filter((company) => company.canCreate)}
                onCancel={() => setCreatingEngagement(false)}
                onSave={async (input) => {
                  await run(async () => {
                    const created = await collaborationApi.create(input);
                    replaceEngagement(created);
                    setSelected(created.id);
                    setQueryId("");
                    setCreatingEngagement(false);
                  }, "Shared engagement created.");
                }}
              />
            )}
            {engagement ? (
              <>
                <section className="ac-stats" aria-label="Query summary">
                  <div>
                    <b>
                      {
                        allQueries.filter(
                          (item) => item.query.status !== "closed",
                        ).length
                      }
                    </b>
                    Open queries
                  </div>
                  <div>
                    <b>
                      {
                        allQueries.filter((item) =>
                          isQueryOverdue(item.query, today),
                        ).length
                      }
                    </b>
                    Overdue
                  </div>
                  <div>
                    <b>
                      {
                        allQueries.filter(
                          (item) => item.query.status === "ready-for-review",
                        ).length
                      }
                    </b>
                    Ready for review
                  </div>
                  <div>
                    <b>
                      {
                        allQueries.filter(
                          (item) =>
                            item.query.blocksCompletion &&
                            item.query.status !== "closed",
                        ).length
                      }
                    </b>
                    Completion blockers
                  </div>
                </section>
                <Members
                  key={engagement.id}
                  engagement={engagement}
                  manages={Boolean(manages)}
                  onChange={replaceEngagement}
                  run={run}
                />
                <div className="ac-toolbar">
                  <Field label="Search shared queries">
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Reference, title, owner or area"
                    />
                  </Field>
                  <Field label="Query filter">
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="all">All queries</option>
                      <option value="overdue">Overdue</option>
                      <option value="blocking">Completion blockers</option>
                      <option value="draft">Drafts</option>
                      <option value="ready-for-review">Ready for review</option>
                      <option value="closed">Closed</option>
                    </select>
                  </Field>
                  {canEdit && (
                    <button
                      className="ac-primary"
                      onClick={() => setCreatingQuery(!creatingQuery)}
                    >
                      Raise shared query
                    </button>
                  )}
                </div>
                {creatingQuery && (
                  <QueryForm
                    owner={user.displayName}
                    onCancel={() => setCreatingQuery(false)}
                    onSave={async (input) => {
                      await run(async () => {
                        const created = await collaborationApi.createQuery(
                          engagement.id,
                          input,
                        );
                        replaceQuery(created);
                        setQueryId(created.query.id);
                        setCreatingQuery(false);
                      }, "Shared query draft saved.");
                    }}
                  />
                )}
                <div className="ac-workspace">
                  <nav className="ac-query-list" aria-label="Shared queries">
                    {visible.length ? (
                      visible.map(({ query }) => (
                        <button
                          className={
                            query.id === queryId
                              ? "ac-query ac-selected"
                              : "ac-query"
                          }
                          key={query.id}
                          onClick={() => setQueryId(query.id)}
                        >
                          <span>
                            {query.reference} · {query.priority}
                          </span>
                          <strong>{query.title}</strong>
                          <span>
                            {statusLabel(query.status)} · Due {query.dueDate}
                          </span>
                          {isQueryOverdue(query, today) && <em>Overdue</em>}
                        </button>
                      ))
                    ) : (
                      <p>No queries match this view.</p>
                    )}
                  </nav>
                  {detail ? (
                    <QueryDetail
                      key={`${engagement.id}:${detail.query.id}`}
                      engagementId={engagement.id}
                      item={detail}
                      canEdit={canEdit}
                      canReview={canReview}
                      onChange={replaceQuery}
                      members={engagement.members}
                      onRefresh={async () =>
                        replaceEngagement(
                          await collaborationApi.get(engagement.id),
                        )
                      }
                      run={run}
                    />
                  ) : (
                    <section className="ac-panel">
                      <h2>Select a query</h2>
                      <p>
                        Choose a query to see its discussion, evidence and
                        client links.
                      </p>
                    </section>
                  )}
                </div>
              </>
            ) : (
              <section className="ac-panel">
                <h2>No shared engagement selected</h2>
                <p>
                  Create an engagement for a company you can access, or ask the
                  engagement owner to add you.
                </p>
              </section>
            )}
          </fieldset>
          {busy && <p role="status">Saving or loading shared data…</p>}
        </>
      )}
    </main>
  );
}

type Run = (action: () => Promise<void>, success?: string) => Promise<boolean>;
function EngagementForm({
  companies,
  onCancel,
  onSave,
}: {
  companies: AuditCompany[];
  onCancel: () => void;
  onSave: (input: {
    entityId: string;
    name: string;
    financialYear: string;
  }) => Promise<void>;
}) {
  const [entityId, setEntityId] = useState(companies[0]?.id ?? "");
  const [name, setName] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onSave({ entityId, name, financialYear });
  };
  return (
    <form className="ac-panel" onSubmit={submit}>
      <h2>New shared engagement</h2>
      <div className="ac-form-grid">
        <Field label="Company">
          <select
            required
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
          >
            <option value="">Select a company</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Engagement name">
          <input
            required
            maxLength={150}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Financial year">
          <input
            required
            maxLength={20}
            placeholder="2025–26"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          />
        </Field>
      </div>
      <div className="ac-actions">
        <button className="ac-primary">Create shared engagement</button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
function Members({
  engagement,
  manages,
  onChange,
  run,
}: {
  engagement: SharedAuditEngagement;
  manages: boolean;
  onChange: (value: SharedAuditEngagement) => void;
  run: Run;
}) {
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<AuditMemberRole>("viewer");
  const chosen = candidates.find((candidate) => candidate.userId === userId);
  return (
    <details className="ac-panel">
      <summary>Engagement team · {engagement.members.length} members</summary>
      <p>
        Membership is explicit. Company access and account permissions are also
        checked by the server.
      </p>
      <ul className="ac-members">
        {engagement.members.map((member) => (
          <li key={member.userId}>
            <span>
              {member.displayName} <small>{member.role}</small>
            </span>
            {manages && (
              <button
                onClick={() =>
                  run(
                    async () =>
                      onChange(
                        await collaborationApi.removeMember(
                          engagement.id,
                          member.userId,
                        ),
                      ),
                    "Engagement member removed.",
                  )
                }
              >
                Remove {member.displayName}
              </button>
            )}
          </li>
        ))}
      </ul>
      {manages && (
        <>
          <button
            onClick={() =>
              run(async () => {
                setCandidates(await collaborationApi.candidates(engagement.id));
                setLoaded(true);
              })
            }
          >
            Load eligible team members
          </button>
          {loaded && (
            <div className="ac-toolbar">
              <Field label="Team member">
                <select
                  value={userId}
                  onChange={(e) => {
                    const next = candidates.find(
                      (item) => item.userId === e.target.value,
                    );
                    setUserId(e.target.value);
                    setRole(next?.eligibleRoles[0] ?? "viewer");
                  }}
                >
                  <option value="">Select a member</option>
                  {candidates.map((candidate) => (
                    <option key={candidate.userId} value={candidate.userId}>
                      {candidate.displayName}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Engagement role">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as AuditMemberRole)}
                >
                  {(chosen?.eligibleRoles ?? ["viewer"]).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                disabled={!chosen}
                onClick={() =>
                  run(
                    async () =>
                      onChange(
                        await collaborationApi.setMember(
                          engagement.id,
                          userId,
                          role,
                        ),
                      ),
                    "Engagement membership saved.",
                  )
                }
              >
                Add or update member
              </button>
            </div>
          )}
        </>
      )}
    </details>
  );
}
function QueryForm({
  owner,
  onCancel,
  onSave,
}: {
  owner: string;
  onCancel: () => void;
  onSave: (input: NewQueryInput) => Promise<void>;
}) {
  const [input, setInput] = useState<NewQueryInput>({
    title: "",
    description: "",
    area: "",
    owner,
    clientContact: "",
    dueDate: "",
    priority: "Normal",
    blocksCompletion: false,
    financialImpact: 0,
  });
  const change = (patch: Partial<NewQueryInput>) =>
    setInput((value) => ({ ...value, ...patch }));
  return (
    <form
      className="ac-panel"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(input);
      }}
    >
      <h2>Raise a shared query</h2>
      <p>
        The title, question, area, contact and due date are visible to holders
        of a client link once issued. Keep internal assessments in internal
        notes.
      </p>
      <div className="ac-form-grid">
        <Field label="Query title (client-facing)">
          <input
            required
            maxLength={200}
            value={input.title}
            onChange={(e) => change({ title: e.target.value })}
          />
        </Field>
        <Field label="Audit area (client-facing)">
          <input
            required
            maxLength={100}
            value={input.area}
            onChange={(e) => change({ area: e.target.value })}
          />
        </Field>
        <Field label="Audit owner">
          <input
            required
            maxLength={150}
            value={input.owner}
            onChange={(e) => change({ owner: e.target.value })}
          />
        </Field>
        <Field label="Client contact">
          <input
            required
            maxLength={150}
            value={input.clientContact}
            onChange={(e) => change({ clientContact: e.target.value })}
          />
        </Field>
        <Field label="Response due date">
          <input
            required
            type="date"
            value={input.dueDate}
            onChange={(e) => change({ dueDate: e.target.value })}
          />
        </Field>
        <Field label="Priority">
          <select
            value={input.priority}
            onChange={(e) =>
              change({ priority: e.target.value as NewQueryInput["priority"] })
            }
          >
            {QUERY_PRIORITIES.map((priority) => (
              <option key={priority}>{priority}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Question and requested evidence (client-facing)">
        <textarea
          required
          maxLength={10000}
          rows={4}
          value={input.description}
          onChange={(e) => change({ description: e.target.value })}
        />
      </Field>
      <label className="ac-check">
        <input
          type="checkbox"
          checked={input.blocksCompletion}
          onChange={(e) => change({ blocksCompletion: e.target.checked })}
        />
        Flag as a completion blocker in this shared register
      </label>
      <p className="ac-help">
        This shared flag does not change browser workpaper stage gates.
      </p>
      <div className="ac-actions">
        <button className="ac-primary">Save shared query draft</button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function QueryDetail({
  engagementId,
  item,
  canEdit,
  canReview,
  onChange,
  members,
  onRefresh,
  run,
}: {
  members: SharedAuditEngagement["members"];
  engagementId: string;
  item: SharedAuditQuery;
  canEdit: boolean;
  canReview: boolean;
  onChange: (value: SharedAuditQuery) => void;
  onRefresh: () => Promise<void>;
  run: Run;
}) {
  const { query, revision, attachments } = item;
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "client">(
    "internal",
  );
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [evidenceReason, setEvidenceReason] = useState("");
  const [links, setLinks] = useState<PortalLinkRecord[]>([]);
  const [linkReady, setLinkReady] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(3);
  const [freshLink, setFreshLink] = useState<{
    url: string;
    expiresAt: string;
  } | null>(null);
  const [linkWarning, setLinkWarning] = useState("");
  const closed = query.status === "closed";
  const authorName = (id: string) =>
    members.find((member) => member.userId === id)?.displayName ??
    (id === getSession()?.user.sub ? getSession()?.user.displayName : id);
  const action = async (value: SharedQueryAction, success: string) =>
    run(
      async () =>
        onChange(await collaborationApi.action(engagementId, query.id, value)),
      success,
    );
  const changeStatus = async (
    status: "ready-for-review" | "closed" | "issued",
  ) => {
    if (
      await action(
        { revision, action: "status", status, reason },
        "Query status updated.",
      )
    )
      setReason("");
  };
  return (
    <article className="ac-panel ac-detail">
      <div className="ac-detail-heading">
        <div>
          <span className="ac-eyebrow">
            {query.reference} · REVISION {revision}
          </span>
          <h2>{query.title}</h2>
        </div>
        <span className="ac-badge">{statusLabel(query.status)}</span>
      </div>
      <p className="ac-question">{query.description}</p>
      <dl className="ac-metadata">
        <div>
          <dt>Audit area</dt>
          <dd>{query.area}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{query.owner}</dd>
        </div>
        <div>
          <dt>Client contact</dt>
          <dd>{query.clientContact}</dd>
        </div>
        <div>
          <dt>Due date</dt>
          <dd>{query.dueDate}</dd>
        </div>
        <div>
          <dt>Priority</dt>
          <dd>{query.priority}</dd>
        </div>
        <div>
          <dt>Completion flag</dt>
          <dd>{query.blocksCompletion ? "Blocks completion" : "Standard"}</dd>
        </div>
      </dl>
      {query.escalationReason && (
        <p className="ac-notice">Escalation: {query.escalationReason}</p>
      )}
      {query.closureConclusion && (
        <p className="ac-notice">
          Reviewer conclusion: {query.closureConclusion}
        </p>
      )}
      <section>
        <h3>Discussion</h3>
        <ol className="ac-thread">
          {query.messages.length ? (
            query.messages.map((message) => (
              <li
                key={message.id}
                className={
                  message.visibility === "internal"
                    ? "ac-internal"
                    : "ac-client"
                }
              >
                <div>
                  <strong>{authorName(message.author)}</strong>
                  <span className="ac-badge">
                    {message.visibility === "internal"
                      ? "Internal only"
                      : "Client-facing"}
                  </span>
                  <time dateTime={message.at}>
                    {new Date(message.at).toLocaleString()}
                  </time>
                </div>
                <p>{message.text}</p>
                {message.evidenceRef && (
                  <small>Evidence reference: {message.evidenceRef}</small>
                )}
              </li>
            ))
          ) : (
            <li>No discussion yet.</li>
          )}
        </ol>
        {canEdit && !closed && (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (
                await action(
                  { revision, action: "message", text, visibility },
                  "Discussion entry saved.",
                )
              )
                setText("");
            }}
          >
            <Field label="Message visibility">
              <select
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value as "internal" | "client")
                }
              >
                <option value="internal">Internal team only</option>
                <option value="client" disabled={query.status === "draft"}>
                  Client-facing thread
                </option>
              </select>
            </Field>
            <Field label="Discussion message">
              <textarea
                required
                maxLength={10000}
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </Field>
            <p className="ac-help">
              {visibility === "client"
                ? "This message is visible to anyone with an active link to this query. No email is sent."
                : "Internal notes are excluded from the client portal."}
            </p>
            <button className="ac-primary">Add discussion entry</button>
          </form>
        )}
      </section>
      <section>
        <h3>Evidence files</h3>
        <p className="ac-help">
          PDF, PNG or JPEG · up to 5 MB each. Uploads keep their original bytes
          and SHA-256 hash. Evidence is unreviewed until a reviewer records a
          decision. Audit acceptance is not a malware scan; check downloads
          before opening.
        </p>
        <div className="ac-files">
          {attachments.map((attachment) => (
            <div className="ac-file" key={attachment.id}>
              <strong>{attachment.name}</strong>
              <span>
                {(attachment.size / 1024).toFixed(1)} KB ·{" "}
                {attachment.visibility === "internal"
                  ? "Internal file"
                  : "Client submission"}{" "}
                · {attachment.review}
              </span>
              <small>
                {authorName(attachment.uploadedBy)} ·{" "}
                {new Date(attachment.uploadedAt).toLocaleString()}
              </small>
              <code>SHA-256 {attachment.sha256}</code>
              {attachment.reviewReason && (
                <p>Evidence review: {attachment.reviewReason}</p>
              )}
              <div className="ac-actions">
                <button
                  onClick={() =>
                    run(async () =>
                      collaborationApi.download(
                        engagementId,
                        query.id,
                        attachment.id,
                        attachment.name,
                      ),
                    )
                  }
                >
                  Download {attachment.name}
                </button>
                {canReview && !closed && (
                  <>
                    <button
                      disabled={!evidenceReason.trim()}
                      onClick={() =>
                        run(async () => {
                          onChange(
                            await collaborationApi.review(
                              engagementId,
                              query.id,
                              attachment.id,
                              {
                                revision,
                                review: "accepted",
                                reason: evidenceReason,
                              },
                            ),
                          );
                          setEvidenceReason("");
                        }, "Evidence accepted.")
                      }
                    >
                      Accept {attachment.name}
                    </button>
                    <button
                      disabled={!evidenceReason.trim()}
                      onClick={() =>
                        run(async () => {
                          onChange(
                            await collaborationApi.review(
                              engagementId,
                              query.id,
                              attachment.id,
                              {
                                revision,
                                review: "rejected",
                                reason: evidenceReason,
                              },
                            ),
                          );
                          setEvidenceReason("");
                        }, "Evidence rejected.")
                      }
                    >
                      Reject {attachment.name}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        {!attachments.length && <p>No evidence uploaded.</p>}
        {canReview && !closed && !!attachments.length && (
          <Field label="Evidence review reason">
            <textarea
              rows={2}
              value={evidenceReason}
              onChange={(e) => setEvidenceReason(e.target.value)}
            />
          </Field>
        )}
        {canEdit && !closed && (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!file) return;
              await run(async () => {
                const content = await prepareEvidence(file);
                onChange(
                  await collaborationApi.upload(engagementId, query.id, {
                    ...content,
                    revision,
                  }),
                );
                setFile(null);
                setFileKey((value) => value + 1);
              }, "Internal evidence uploaded for review.");
            }}
          >
            <Field label="Upload internal evidence">
              <input
                key={fileKey}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>
            <button disabled={!file}>Upload evidence</button>
          </form>
        )}
      </section>
      {canEdit && (
        <section>
          <h3>Review and follow-up</h3>
          <p className="ac-help">
            An independent reviewer must assess accepted evidence before
            closure. Closing a query does not sign off an audit procedure.
          </p>
          <Field label="Action reason or closure conclusion">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div className="ac-actions">
            {query.status === "draft" ? (
              <button
                className="ac-primary"
                onClick={() =>
                  action(
                    { revision, action: "issue" },
                    "Query issued. Create a client link separately to share it.",
                  )
                }
              >
                Issue shared query
              </button>
            ) : closed ? (
              <button
                disabled={!reason.trim()}
                onClick={async () => {
                  if (
                    await action(
                      { revision, action: "reopen", reason },
                      "Query reopened.",
                    )
                  )
                    setReason("");
                }}
              >
                Reopen query
              </button>
            ) : (
              <>
                <button
                  disabled={!reason.trim()}
                  onClick={async () => {
                    if (
                      await action(
                        { revision, action: "escalate", reason },
                        "Escalation recorded. No notification was sent.",
                      )
                    )
                      setReason("");
                  }}
                >
                  Record escalation
                </button>
                {query.status !== "ready-for-review" && (
                  <button onClick={() => changeStatus("ready-for-review")}>
                    Submit for review
                  </button>
                )}
                {query.status === "ready-for-review" && (
                  <>
                    <button
                      disabled={!reason.trim()}
                      onClick={() => changeStatus("issued")}
                    >
                      Request further response
                    </button>
                    {canReview && (
                      <button
                        className="ac-primary"
                        disabled={reason.replace(/\s/g, "").length < 20}
                        onClick={() => changeStatus("closed")}
                      >
                        Close after independent review
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>
      )}
      {canEdit && query.status !== "draft" && (
        <section>
          <h3>Restricted client links</h3>
          <p>
            Anyone holding a link can read this query's client-facing details
            and respond while it is open. Link access does not verify the
            person's identity. Share only with your intended client contact.
          </p>
          <p className="ac-help">
            Internal notes, review conclusions, other queries and workpaper
            drafts are excluded. Creating a link does not send an email.
          </p>
          {!closed && (
            <>
              <Field label="Client link expiry">
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                >
                  {[1, 2, 3, 5, 7].map((days) => (
                    <option key={days} value={days}>
                      {days} {days === 1 ? "day" : "days"}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                onClick={() =>
                  run(async () => {
                    const result = await collaborationApi.createLink(
                      engagementId,
                      query.id,
                      revision,
                      expiresInDays,
                    );
                    setFreshLink({
                      url: `${window.location.origin}${window.location.pathname}#/audit-portal/${result.token}`,
                      expiresAt: result.expiresAt,
                    });
                    setLinkWarning("");
                    try {
                      await onRefresh();
                      setLinks(
                        await collaborationApi.links(engagementId, query.id),
                      );
                      setLinkReady(true);
                    } catch {
                      setLinkWarning(
                        "The link was created, but the latest query revision could not be loaded. Refresh shared queries before another change.",
                      );
                    }
                  }, "Client link created. Copy it now; it will not be displayed again after leaving this query.")
                }
              >
                Create client access link
              </button>
            </>
          )}
          {freshLink && (
            <div className="ac-link-box">
              <strong>Copy this link now</strong>
              <Field label="New client access link">
                <input
                  readOnly
                  value={freshLink.url}
                  onFocus={(e) => e.target.select()}
                />
              </Field>
              <p>
                Expires {new Date(freshLink.expiresAt).toLocaleString()}. Treat
                this URL as a secret.
              </p>
              <button onClick={() => setFreshLink(null)}>Hide link</button>
            </div>
          )}
          {linkWarning && (
            <p role="alert" className="ac-error">
              {linkWarning}
            </p>
          )}
          <button
            onClick={() =>
              run(async () => {
                setLinks(await collaborationApi.links(engagementId, query.id));
                setLinkReady(true);
              })
            }
          >
            Show link expiry and revocation status
          </button>
          {linkReady && (
            <ul className="ac-links">
              {links.length ? (
                links.map((link) => (
                  <li key={link.id}>
                    <span>
                      Expires {new Date(link.expiresAt).toLocaleString()} ·{" "}
                      {link.revokedAt
                        ? "Revoked"
                        : new Date(link.expiresAt).getTime() < Date.now()
                          ? "Expired"
                          : "Active"}
                    </span>
                    {!link.revokedAt && (
                      <button
                        onClick={() =>
                          run(async () => {
                            onChange(
                              await collaborationApi.revokeLink(
                                engagementId,
                                query.id,
                                link.id,
                                revision,
                              ),
                            );
                            setFreshLink(null);
                            setLinks(
                              await collaborationApi.links(
                                engagementId,
                                query.id,
                              ),
                            );
                          }, "Client access link revoked.")
                        }
                      >
                        Revoke link
                      </button>
                    )}
                  </li>
                ))
              ) : (
                <li>No links created.</li>
              )}
            </ul>
          )}
        </section>
      )}
    </article>
  );
}
